import { Button, Card, Input } from "@dawa/ui";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import { useState } from "react";
import { motion } from "framer-motion";
import StatsChart from "../components/StatsChart";
import { features } from "../data/features";

const testimonials = [
  {
    quote:
      "DawaRedact a transformé notre création de contenu. Nous avons vu une hausse de 40% du trafic organique.",
    author: "Sarah Johnson",
    role: "Marketing Director",
    company: "TechCorp",
  },
  {
    quote:
      "Le contenu généré par l'IA est naturel et engageant, comme une équipe de rédacteurs experts.",
    author: "Michael Chen",
    role: "SEO Specialist",
    company: "Digital Growth",
  },
];

export default function Landing() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-bg">
      <header className="mx-auto max-w-5xl p-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">DawaRedact</h1>
          <Button
            variant="ghost"
            size="sm"
            className="md:hidden"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
            aria-expanded={open}
            aria-controls="landing-mobile-nav"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </Button>
          <nav className="hidden gap-2 md:flex" aria-label="Navigation principale">
            <Link to="/auth/login">
              <Button variant="secondary">Se connecter</Button>
            </Link>
            <Link to="/auth/signup">
              <Button>S'inscrire</Button>
            </Link>
          </nav>
        </div>
        <nav
          id="landing-mobile-nav"
          aria-label="Navigation principale"
          className={`${open ? "flex" : "hidden"} flex-col gap-2 pt-4 md:hidden`}
        >
          <Link to="/auth/login">
            <Button variant="secondary" className="w-full">
              Se connecter
            </Button>
          </Link>
          <Link to="/auth/signup">
            <Button className="w-full">S'inscrire</Button>
          </Link>
        </nav>
      </header>

      <section className="bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white">
        <div className="mx-auto max-w-4xl px-4 py-32 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-5xl font-extrabold tracking-tight"
          >
            La plateforme de rédaction SEO nouvelle génération
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-6 text-lg text-indigo-100"
          >
            Générez des contenus optimisés en quelques secondes grâce à notre moteur IA.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row"
          >
            <Link to="/auth/signup">
              <Button>Commencer gratuitement</Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline">Demander une démo</Button>
            </Link>
            <Link to="/auth/login">
              <Button variant="secondary">Déjà inscrit ? Connexion</Button>
            </Link>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-4 text-sm text-indigo-100"
          >
            Essai gratuit 14 jours – pas de carte de crédit requise
          </motion.p>
        </div>
      </section>

      <section aria-labelledby="logos-heading" className="bg-surface">
        <h3 id="logos-heading" className="sr-only">
          Entreprises partenaires
        </h3>
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-center gap-8 p-8">
          {[
            "/logos/logo1.svg",
            "/logos/logo2.svg",
            "/logos/logo3.svg",
            "/logos/logo4.svg",
            "/logos/logo5.svg",
          ].map((logo, i) => (
            <img key={logo} src={logo} alt={`Logo fictif ${i + 1}`} className="h-10 w-auto" />
          ))}
        </div>
      </section>

      <section aria-labelledby="metrics-heading" className="mx-auto mt-16 max-w-5xl px-4">
        <h3 id="metrics-heading" className="text-center text-3xl font-extrabold">
          Des résultats qui comptent
        </h3>
        <div className="mt-10 grid grid-cols-1 gap-8 text-center sm:grid-cols-3">
          <div>
            <p className="text-4xl font-extrabold text-accent">500+</p>
            <p className="mt-2 text-muted">entreprises accompagnées</p>
          </div>
          <div>
            <p className="text-4xl font-extrabold text-accent">2M</p>
            <p className="mt-2 text-muted">mots générés</p>
          </div>
          <div>
            <p className="text-4xl font-extrabold text-accent">97%</p>
            <p className="mt-2 text-muted">de satisfaction client</p>
          </div>
        </div>
      </section>

      <section
        aria-labelledby="features-heading"
        className="mx-auto mt-32 grid max-w-5xl gap-8 px-4 sm:grid-cols-2 md:grid-cols-3"
      >
        <h3 id="features-heading" className="sr-only">
          Fonctionnalités principales
        </h3>
        {features.map((f) => (
          <motion.div
            key={f.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Card className="p-6 text-center">
              <f.icon className="mx-auto h-8 w-8 text-accent" />
              <h3 className="mt-4 text-lg font-semibold">{f.title}</h3>
              <p className="mt-2 text-sm text-muted">{f.description}</p>
            </Card>
          </motion.div>
        ))}
      </section>

      <section aria-labelledby="stats-heading" className="mx-auto mt-32 max-w-5xl px-4">
        <h3 id="stats-heading" className="text-center text-3xl font-extrabold">
          Performance en temps réel
        </h3>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-10"
        >
          <StatsChart />
        </motion.div>
      </section>

      <section aria-labelledby="testimonials-heading" className="mx-auto mt-32 max-w-5xl px-4">
        <h3 id="testimonials-heading" className="text-center text-3xl font-extrabold">
          Ils nous font confiance
        </h3>
        <div className="mt-10 grid gap-8 md:grid-cols-2">
          {testimonials.map((t) => (
            <motion.div
              key={t.author}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card className="p-6">
                <p className="italic text-muted">"{t.quote}"</p>
                <p className="mt-4 font-semibold">{t.author}</p>
                <p className="text-sm text-muted">
                  {t.role}, {t.company}
                </p>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>
      <section
        aria-labelledby="newsletter-heading"
        className="mx-auto mt-32 max-w-3xl px-4 text-center"
      >
        <h3 id="newsletter-heading" className="text-3xl font-extrabold">
          Restez informé
        </h3>
        <p className="mt-2 text-muted">
          Recevez nos dernières astuces CRO et SEO directement dans votre boîte mail.
        </p>
        <form
          className="mx-auto mt-6 flex flex-col items-center gap-4 sm:flex-row"
          onSubmit={(e) => e.preventDefault()}
        >
          <Input
            type="email"
            required
            placeholder="Votre email"
            className="flex-1"
            aria-label="Adresse email"
          />
          <Button type="submit">S'abonner</Button>
        </form>
      </section>
      <Footer />
    </div>
  );
}
